import { useCallback } from 'react';
import { useAppUpdatesContext } from '@app/app/src/AppContext';
import {
  filtersToUrlParams,
  createFilterUrlSearch,
  urlParamsToFilters,
  type FilterState,
} from '../utils/urlFilterUtils';

export interface UseUrlFilterSyncReturn {
  // eslint-disable-next-line no-unused-vars
  syncFiltersToUrl: (overrides?: Partial<FilterState>) => void;
  getFiltersFromUrl: () => Partial<FilterState>;
}

/**
 * Hook to synchronize committed filter state with URL parameters.
 */
export const useUrlFilterSync = (): UseUrlFilterSyncReturn => {
  const { state } = useAppUpdatesContext();

  const syncFiltersToUrl = useCallback(
    (overrides?: Partial<FilterState>) => {
      // eslint-disable-next-line no-unused-vars
      const { committedFiltersVersion: _committedFiltersVersion, ...filters } =
        state.updates.committedFilters;

      const currentFilters: FilterState = {
        ...filters,
        cursor: state.updates.pagination.cursor,
        limit: state.updates.pagination.limit,
        ...overrides,
      };

      const urlParams = filtersToUrlParams(currentFilters);
      const searchString = createFilterUrlSearch(urlParams);
      const newUrl = `${window.location.pathname}${searchString}`;

      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', newUrl);
      }
    },
    [state.updates.committedFilters, state.updates.pagination],
  );

  const getFiltersFromUrlCallback = useCallback(
    (): Partial<FilterState> => urlParamsToFilters(),
    [],
  );

  return {
    syncFiltersToUrl,
    getFiltersFromUrl: getFiltersFromUrlCallback,
  };
};
